import { Button, TextInput } from '@mantine/core';
import { type FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { HttpNewForm } from '@/api';
import { Section, SectionBody } from '@/components/Ui/Section';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { usePlanRetention } from '@/hooks/usePlanRetention';
import type { SiteMethod } from '@/lib/form-site';
import { createForm, defaultRecipient, starterSpecToml, useAddressPool } from './createForm';
import classes from './FormCreate.module.css';
import { MethodChoice } from './MethodChoice';

/**
 * The guided start for an account without forms: name and method, everything else default.
 * Renders its own Section ("Set up your first form").
 */
export function QuickCreate() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();
  const ids = { name: useId(), method: useId() };
  const { pool } = useAddressPool();
  const retention = usePlanRetention();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [method, setMethod] = useState<SiteMethod>('hosted');
  const [busy, setBusy] = useState(false);

  const recipient = defaultRecipient(pool);
  const summary = [
    recipient
      ? t('create.quick.notifyAt', { email: recipient.email })
      : t('create.quick.notifyNone'),
    t('create.captchaOn'),
    t(`create.quick.after.${method}`),
  ].join(' ');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError(t('create.nameRequired'));
      document.getElementById(ids.name)?.focus();
      return;
    }
    const body: HttpNewForm = {
      title: name.trim(),
      method,
      check_challenge: true,
      // The backend refuses a new form without a retention period.
      retention_days: retention.max,
      ...(method !== 'own' ? { specs: starterSpecToml('contact') } : {}),
    };
    setBusy(true);
    try {
      const form = await createForm(body, [], t);
      nav(createLanguageURL(`/account/forms/${form.id}`));
    } catch {
      setBusy(false);
    }
  };

  const moreOptions = () => {
    const query = new URLSearchParams({ method });
    if (name.trim()) query.set('name', name.trim());
    nav(createLanguageURL(`/account/forms/new?${query.toString()}`));
  };

  return (
    <Section title={t('create.quick.title')} subtitle={t('create.quick.subtitle')}>
      <SectionBody>
        <form className={classes.quick} onSubmit={submit} noValidate>
          <TextInput
            id={ids.name}
            label={t('create.nameLabel')}
            placeholder={t('create.quick.namePlaceholder')}
            value={name}
            onChange={(e) => {
              setName(e.currentTarget.value);
              setNameError(null);
            }}
            error={nameError}
          />
          <fieldset className={classes.plainFieldset}>
            <legend className={classes.quickLegend}>{t('create.quick.methodLegend')}</legend>
            <MethodChoice value={method} onChange={setMethod} name={ids.method} compact />
          </fieldset>
          <span className={classes.sub}>{summary}</span>
          <div className={classes.actions}>
            <Button type="submit" variant="filled" loading={busy}>
              {t('create.submit')}
            </Button>
            <Button variant="default" onClick={moreOptions} disabled={busy}>
              {t('create.quick.more')}
            </Button>
          </div>
        </form>
      </SectionBody>
    </Section>
  );
}
